import * as React from "react"

import { cn } from "@/lib/utils"

function Input({ className, type, min, max, value, onChange, ...props }: React.ComponentProps<"input">) {
  // 仅在 type 为 number 且有 min 或 max 时启用校验
  const shouldValidate = type === "number" && (min !== undefined || max !== undefined)
  
  // 内部状态管理输入值
  const [inputValue, setInputValue] = React.useState(value)

  // 同步外部 value 变化
  React.useEffect(() => {
    setInputValue(value)
  }, [value])

  // 校验并修正值
  const clampValue = React.useCallback((val: string | number | readonly string[] | undefined): string | number | readonly string[] | undefined => {
    if (!shouldValidate || val === undefined || val === '') return val
    
    const numValue = typeof val === 'string' ? parseFloat(val) : Number(val)
    if (isNaN(numValue)) return val
    
    const minNum = min !== undefined ? Number(min) : -Infinity
    const maxNum = max !== undefined ? Number(max) : Infinity
    
    // 如果值超出范围，返回修正后的边界值
    if (numValue < minNum || numValue > maxNum) {
      return String(Math.max(minNum, Math.min(maxNum, numValue)))
    }
    
    return val
  }, [shouldValidate, min, max])

  // 当 min 或 max 变化时，重新校验当前值
  React.useEffect(() => {
    if (!shouldValidate) return
    
    const clampedValue = clampValue(inputValue)
    
    // 如果值被修正了，更新状态并通知父组件
    if (clampedValue !== inputValue && onChange) {
      setInputValue(clampedValue)
      
      // 创建一个模拟的事件对象
      const mockEvent = {
        target: { value: String(clampedValue) },
        currentTarget: { value: String(clampedValue) },
      } as React.ChangeEvent<HTMLInputElement>
      onChange(mockEvent)
    }
  }, [min, max, shouldValidate, inputValue, clampValue, onChange])

  // 处理输入失焦时的校验
  const handleBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (!shouldValidate) {
      props.onBlur?.(e)
      return
    }
    
    const clampedValue = clampValue(e.target.value)
    
    // 如果值被修正了，更新状态并通知父组件
    if (clampedValue !== e.target.value) {
      setInputValue(clampedValue)
      
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: String(clampedValue) },
          currentTarget: { ...e.currentTarget, value: String(clampedValue) },
        } as unknown as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
    }
    
    props.onBlur?.(e)
  }, [shouldValidate, clampValue, onChange, props])

  // 处理输入变化
  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    
    if (shouldValidate) {
      // 立即校验并修正值
      const clampedValue = clampValue(newValue)
      setInputValue(clampedValue)
      
      // 如果值被修正了，通知父组件修正后的值
      if (clampedValue !== newValue) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: String(clampedValue) },
          currentTarget: { ...e.currentTarget, value: String(clampedValue) },
        } as React.ChangeEvent<HTMLInputElement>
        onChange?.(syntheticEvent)
      } else {
        onChange?.(e)
      }
    } else {
      onChange?.(e)
    }
  }, [shouldValidate, clampValue, onChange])

  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-0",
        "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
        className
      )}
      min={min}
      max={max}
      value={shouldValidate ? inputValue : value}
      onChange={shouldValidate ? handleChange : onChange}
      onBlur={handleBlur}
      {...props}
    />
  )
}

export { Input }
